import { Stack, Typography, useTheme } from "@mui/material";
import type { FC } from "react";
import { usePortfolioDocumentsQuery } from "web_myplann/Api/Portfolio/Queries/usePortfolioDocumentsQuery";
import { usePortfolioIdReportingQuery } from "web_myplann/Api/Portfolio/Queries/usePortfolioIdReportingQuery";
import type {
	LeasingDetailDataDTO,
	LoanDetailDataDTO,
	LoanForBusinessDetailDataDTO,
} from "web_myplann/Infrastructure/Api/Api";
import {
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";
import { DashedButton } from "web_myplann/Portfolio/Components/Redesign/Detail/DashedButton";
import { DetailCard } from "web_myplann/Portfolio/Components/Redesign/Detail/DetailCard";
import { DetailLoader } from "web_myplann/Portfolio/Components/Redesign/Detail/DetailLoader";
import { DetailRow } from "web_myplann/Portfolio/Components/Redesign/Detail/DetailRow";
import { DetailValueChart } from "web_myplann/Portfolio/Components/Redesign/Detail/DetailValueChart";
import { RelatedSectionRedesign } from "web_myplann/Portfolio/Components/Redesign/Detail/RelatedSectionRedesign";
import { CoDebtorsList } from "web_myplann/Portfolio/Components/Redesign/Detail/Sections/CoDebtorsList";
import { LoanAllBasicInfoDetails } from "web_myplann/Portfolio/Components/Redesign/Detail/Sections/LoanAllBasicInfoDetails";
import { LoanParameters } from "web_myplann/Portfolio/Components/Redesign/Detail/Sections/LoanParameters";
import { PortfolioBasicInfoRows } from "web_myplann/Portfolio/Components/Redesign/Detail/Sections/PortfolioBasicInfoRows";
import { calculateGraphData } from "web_myplann/Portfolio/Helpers/calculateGraphData";
import { getPortfolioValueDate } from "web_myplann/Portfolio/Helpers/portfolioDetailFormatters";
import { PortfolioItemKind } from "web_myplann/Portfolio/Types/portfolioItemKind";
import { monthYearFormatter } from "web_myplann/Shared/Utils/dateFormatters";
import type { PortfolioDetailProps } from "web_myplann/Utils/types";

const PageResources = Resources.PortfolioDetail;

export const LoanAllRedesign: FC<PortfolioDetailProps> = ({
	baseData,
	currencySign,
	onUpdateValue,
}) => {
	const { t } = useResource();
	const theme = useTheme();

	const { data: documents, isLoading: isDocumentsLoading } =
		usePortfolioDocumentsQuery(baseData.id);
	const { data: reporting, isLoading: isReportingLoading } =
		usePortfolioIdReportingQuery(baseData.id);

	if (isDocumentsLoading || isReportingLoading) {
		return <DetailLoader hasChart />;
	}

	const { daysData, totalValue, totalInvestedValue, showChartInfo } =
		calculateGraphData(
			reporting?.reportingData,
			reporting?.summary ?? undefined,
		);

	const portfolioData = baseData.data as LoanForBusinessDetailDataDTO &
		LoanDetailDataDTO &
		LeasingDetailDataDTO;

	return (
		<>
			{daysData.length > 0 ? (
				<DetailValueChart
					loanChart
					totalValue={totalValue}
					totalInvestedValue={totalInvestedValue}
					totalEvaluationPercent={
						reporting?.summary?.totalEvaluationPercent ?? 0
					}
					daysData={daysData}
					currencySign={currencySign}
					valueDate={getPortfolioValueDate(daysData, baseData)}
					fixationEndDate={portfolioData.fixationEndDate}
					showChartInfo={showChartInfo}
					onEditValue={onUpdateValue}
					bottomComponent={
						<LoanParameters
							originalObligationAmount={portfolioData.originalObligationAmount}
							maturityLengthMonths={baseData.maturityLengthMonths}
							interestRate={portfolioData.interestRate}
							installment={portfolioData.installment}
							paymentFrequency={portfolioData.paymentFrequency}
							currencySign={currencySign}
						/>
					}
				/>
			) : (
				<Stack bgcolor={theme.palette.background.paper}>
					{onUpdateValue && (
						<Stack px={theme.spacingTokens.lg} pt={theme.spacingTokens.md}>
							<DashedButton onClick={onUpdateValue}>
								<Typography
									variant="bodyMedium"
									color={theme.palette.blue[600]}
								>
									{t(
										Resources.PortfolioDetail.SharedLabels.ValueDrawer
											.fillTitle,
									)}
								</Typography>
							</DashedButton>
						</Stack>
					)}
					<LoanParameters
						originalObligationAmount={portfolioData.originalObligationAmount}
						maturityLengthMonths={baseData.maturityLengthMonths}
						interestRate={portfolioData.interestRate}
						installment={portfolioData.installment}
						paymentFrequency={portfolioData.paymentFrequency}
						currencySign={currencySign}
					/>
				</Stack>
			)}

			<DetailCard label={t(Resources.DataCard.BasicInfo.title)}>
				<PortfolioBasicInfoRows baseData={baseData} />
				<DetailRow
					label={t(PageResources.Loan.fixationEndDate)}
					value={monthYearFormatter(portfolioData.fixationEndDate)}
				/>
			</DetailCard>
			<CoDebtorsList coDebtors={portfolioData.coDebtors ?? []} />
			<LoanAllBasicInfoDetails baseData={baseData} />

			<RelatedSectionRedesign
				documents={documents?.data}
				related={baseData.related}
				itemKind={PortfolioItemKind.Portfolio}
				portfolioId={baseData.id}
			/>
		</>
	);
};
